import {MoveDialogTrigger} from '@github-ui/drag-and-drop'
import {commitUnpinIssueMutation} from '@github-ui/issue-viewer/commitUnpinIssueMutation'
import {getIssueStateIcon} from '@github-ui/list-view-items-issues-prs/StateIcon'
import {userHovercardPath} from '@github-ui/paths'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {CommentIcon, GrabberIcon, KebabHorizontalIcon, PinSlashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, Link, RelativeTime} from '@primer/react'
import {Octicon} from '@primer/styled-react/deprecated'
import {clsx} from 'clsx'
import {useCallback, useRef} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'

import {ERRORS} from '../../constants/errors'
import type {PinnedIssueIssue$key} from './__generated__/PinnedIssueIssue.graphql'
import styles from './PinnedIssue.module.css'

type PinnedIssueProps = {
  issue: PinnedIssueIssue$key
}

export function PinnedIssue({issue}: PinnedIssueProps) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment PinnedIssueIssue on Issue {
        id
        title
        titleHTML
        url
        createdAt
        state
        stateReason
        number
        author {
          login
          url
        }
        totalCommentsCount
        repository {
          viewerCanPinIssues
        }
      }
    `,
    issue,
  )

  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()

  const unpin = useCallback(() => {
    if (!data) return

    commitUnpinIssueMutation({
      environment,
      input: {issueId: data.id},
      onCompleted: () => {},
      onError: () => {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: ERRORS.couldNotUnpinIssue,
        })
      },
    })
  }, [addToast, data, environment])

  const editButtonRef = useRef<HTMLButtonElement>(null)
  if (!data) return <></>

  const stateOrReason = data.state === 'CLOSED' && data.stateReason === 'NOT_PLANNED' ? 'NOT_PLANNED' : data.state
  const state = getIssueStateIcon(stateOrReason)
  const commentCount = data.totalCommentsCount ?? 0
  const createdAt = new Date(data.createdAt)

  return (
    <div className={styles.PinnedIssueContainer}>
      <div className={styles.contentContainer}>
        <div className={styles.titleOptionsContainer}>
          <Link aria-label={`View ${data.title}`} className={clsx('css-truncate', styles.Link)} href={data.url} muted>
            <Octicon
              sx={{
                color: state.color,
              }}
              icon={state.icon}
              aria-label={state.description}
              className={styles.Octicon}
            />
            <VerifiedHTMLText html={data.titleHTML as SafeHTMLString} />
          </Link>
          {data.repository.viewerCanPinIssues && (
            <ActionMenu anchorRef={editButtonRef}>
              <ActionMenu.Anchor>
                <IconButton
                  size="small"
                  icon={KebabHorizontalIcon}
                  variant="invisible"
                  aria-label="Pinned issue options"
                  className={styles.IconButton}
                />
              </ActionMenu.Anchor>
              <ActionMenu.Overlay width="medium">
                <ActionList>
                  {data && (
                    <ActionList.Item onSelect={unpin} aria-label={`Unpin issue #${data.number}, ${data.title}`}>
                      <ActionList.LeadingVisual>
                        <Octicon icon={PinSlashIcon} />
                      </ActionList.LeadingVisual>
                      Unpin
                    </ActionList.Item>
                  )}
                  <MoveDialogTrigger Component={ActionListItemReorder} returnFocusRef={editButtonRef} />
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          )}
        </div>
        <div className={styles.metadataContainer}>
          <span className={styles.issueMetadata}>
            #{data.number} &middot;{' '}
            {data.author && (
              <Link
                aria-label={`View ${data.author.login} profile`}
                href={data.author.url}
                data-hovercard-url={userHovercardPath({owner: data.author.login})}
                muted
              >
                {data.author.login}
              </Link>
            )}{' '}
            {/* Added whitespace:normal to prevent the cards becoming different sizes to accomodate the timestamp length */}
            <span>opened </span>
            <RelativeTime date={createdAt}>
              on {createdAt.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}
            </RelativeTime>
          </span>
          {commentCount > 0 && (
            <div className={styles.commentCountContainer}>
              <Octicon icon={CommentIcon} aria-label={`${commentCount} comments`} className={styles.commentIcon} />
              <span className={styles.commentCountText}>{commentCount}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// onClick is set automatically by the MoveDialogTrigger component
function ActionListItemReorder({onClick}: {onClick?: () => void}) {
  return (
    <ActionList.Item onSelect={onClick}>
      <ActionList.LeadingVisual>
        <Octicon icon={GrabberIcon} />
      </ActionList.LeadingVisual>
      Advanced move...
    </ActionList.Item>
  )
}

try{ PinnedIssue.displayName ||= 'PinnedIssue' } catch {}
try{ ActionListItemReorder.displayName ||= 'ActionListItemReorder' } catch {}